import { css } from "../../../styled-system/css";
import { hstack, vstack } from "../../../styled-system/patterns";
import { FootLength, FootWidth } from "../../api/schemas";
import leftFoot1x from "../../assets/leftFoot@1x.png";
import leftFoot2x from "../../assets/leftFoot@2x.png";
import leftFoot3x from "../../assets/leftFoot@3x.png";
import leftFoot4x from "../../assets/leftFoot@4x.png";
import rightFoot1x from "../../assets/rightFoot@1x.png";
import rightFoot2x from "../../assets/rightFoot@2x.png";
import rightFoot3x from "../../assets/rightFoot@3x.png";
import rightFoot4x from "../../assets/rightFoot@4x.png";
import { PageTitle } from "../../components/PageTitle";
import { SquareButton } from "../../components/buttons/squareButton/SquareButton";
import { SelectInput } from "../../components/input/selectInput/SelectInput";
import { WidthAndSizeFormValues } from "../../types/form";
import { Path } from "../../types/route";

interface SelectWidthAndSizeProps {
  footLengths: FootLength[];
  footWidths: FootWidth[];
  path: Path;
  setPath: (path: Path) => void;
  value: WidthAndSizeFormValues;
  tmpValue: WidthAndSizeFormValues;
  onChange: (widthAndSize: WidthAndSizeFormValues) => void;
  isNextButtonDisabled: boolean;
}

interface WidthAndSizeInputsProps {
  footLengths: FootLength[];
  footWidths: FootWidth[];
  side: "right" | "left";
  widthValue: string;
  sizeValue: string;
  widthAndSizeValues: WidthAndSizeFormValues;
  onChange: (widthAndSize: WidthAndSizeFormValues) => void;
}
function WidthAndSizeInputs({
  footLengths,
  footWidths,
  side,
  widthValue,
  sizeValue,
  widthAndSizeValues,
  onChange,
}: WidthAndSizeInputsProps) {
  return (
    <>
      <div
        className={css({
          position: "relative",
          width: "100%",
          mb: 4,
          textAlign: "center",
        })}
      >
        <span
          className={css({
            textStyle: "span.m.bold",
            position: "relative",
            display: "inline-block",
            bg: "black.100",
            zIndex: 2,
            pl: 1,
            pr: 1,
          })}
        >
          {side === "right" ? "右" : "左"}足の幅と足長を選んでください
        </span>
        <hr
          className={css({
            position: "absolute",
            top: "50%",
            width: "100%",
            zIndex: 1,
          })}
        />
      </div>

      <div className={vstack({ gap: "24px 0", width: "100%" })}>
        <div className={hstack({ gap: 0, width: "100%" })}>
          <label
            htmlFor={`${side}-width`}
            className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
          >
            幅(ワイズ)
          </label>
          <div className={css({ width: "100%" })}>
            <SelectInput
              id={`${side}-width`}
              value={widthValue}
              onChange={(e) => {
                onChange({
                  ...widthAndSizeValues,
                  [`${side}Width`]: e.target.value,
                });
              }}
              options={footWidths.map((footWidth) => {
                return {
                  value: footWidth.value,
                  label: footWidth.value,
                };
              })}
            />
          </div>
        </div>

        <div className={hstack({ gap: 0, width: "100%" })}>
          <label
            htmlFor={`${side}-size`}
            className={css({ textStyle: "span.m.bold", minWidth: "99px" })}
          >
            足長(サイズ)
          </label>
          <div className={css({ width: "100%" })}>
            <SelectInput
              id={`${side}-size`}
              value={sizeValue}
              onChange={(e) => {
                onChange({
                  ...widthAndSizeValues,
                  [`${side}Size`]: e.target.value,
                });
              }}
              options={footLengths.map((footLength) => {
                return {
                  value: footLength.value,
                  label: footLength.value,
                };
              })}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export function SelectWidthAndSize({
  footLengths,
  footWidths,
  path,
  setPath,
  value,
  tmpValue,
  onChange,
  isNextButtonDisabled,
}: SelectWidthAndSizeProps) {
  return (
    <div className={vstack({ width: "100%", gap: 0 })}>
      <div className={css({ width: "100%", maxWidth: "760px", px: 3 })}>
        <div className={css({ mb: 3 })}>
          <PageTitle>幅・足長選択</PageTitle>
        </div>
        <div className={css({ mb: 5 })}>
          <img
            alt="右足"
            srcSet={`${rightFoot1x} 375w, ${rightFoot2x} 750w, ${rightFoot3x} 1125w, ${rightFoot4x} 1500w`}
            className={css({
              maxWidth: "180px",
              display: "block",
              mx: "auto",
              mb: 3,
            })}
          />
          <WidthAndSizeInputs
            footLengths={footLengths}
            footWidths={footWidths}
            side="right"
            widthValue={value.rightWidth}
            sizeValue={value.rightSize}
            widthAndSizeValues={value}
            onChange={onChange}
          />
        </div>
        <div>
          <img
            alt="左足"
            srcSet={`${leftFoot1x} 375w, ${leftFoot2x} 750w, ${leftFoot3x} 1125w, ${leftFoot4x} 1500w`}
            className={css({
              maxWidth: "180px",
              display: "block",
              mx: "auto",
              mb: 3,
            })}
          />
          <WidthAndSizeInputs
            footLengths={footLengths}
            footWidths={footWidths}
            side="left"
            widthValue={value.leftWidth}
            sizeValue={value.leftSize}
            widthAndSizeValues={value}
            onChange={onChange}
          />
        </div>
      </div>

      <div
        className={css({
          bg: "black.100",
          width: "100%",
          px: 3,
          position: "fixed",
          bottom: 0,
          left: 0,
          pt: 2,
          height: "134px",
          boxShadow: "0px 4px 15px 0px #00000040",
          zIndex: "stickeyFooter",
        })}
      >
        <div
          className={css({
            width: "100%",
            maxWidth: "760px",
            mx: "auto",
            textAlign: "center",
          })}
        >
          <SquareButton
            size="full"
            variant="contained"
            color="primary"
            onClick={() => {
              setPath("confirm");
            }}
            disabled={isNextButtonDisabled}
          >
            {path === "select_width_and_size" ? "次へ" : "変更する"}
          </SquareButton>
          <button
            className={css({ mt: 2, textStyle: "button.m" })}
            onClick={() => {
              if (path === "select_width_and_size") {
                setPath("select_color");
              } else {
                onChange(tmpValue);
                setPath("confirm");
              }
            }}
          >
            戻る
          </button>
        </div>
      </div>
    </div>
  );
}
